import { 
  CalendarEvent, 
  CalendarQueryOptions, 
  WeekInfo, 
  GoogleCalendarResponse,
  GoogleCalendarEventItem,
  EventStatus,
  AttendeeResponseStatus,
  AppError 
} from '../types';
import { AuthManager } from '../auth/AuthManager';
import { CONFIG } from '../config';

/**
 * Gestor del calendario que maneja la obtención y procesamiento de eventos
 * 
 * IMPORTANTE: Esta clase está configurada para mostrar SIEMPRE los calendarios 
 * del propietario (luis.rioja11@gmail.com) independientemente de qué usuario 
 * autorizado inicie sesión.
 * 
 * REQUISITO: El propietario debe compartir sus calendarios con los usuarios 
 * autorizados en la whitelist para que puedan verlos.
 */
export class CalendarManager {
  private currentWeekStart: Date;
  private authManager: AuthManager;

  constructor(authManager: AuthManager) {
    this.authManager = authManager;
    this.currentWeekStart = this.getMondayOfCurrentWeek();
  }

  /**
   * Obtiene la lista de calendarios del usuario
   */
  async getUserCalendars(): Promise<any[]> {
    try {
      console.log('📅 Obteniendo lista de calendarios del usuario...');
      
      const gapi = this.authManager.getGapiClient();
      if (!gapi) {
        throw new Error('Cliente de Google API no disponible');
      }

      console.log('🔍 gapi.client:', gapi);
      console.log('🔍 gapi.client.calendar:', gapi.calendar);
      
      if (!gapi.calendar) {
        throw new Error('gapi.client.calendar no está disponible');
      }

      const response = await gapi.calendar.calendarList.list({
        minAccessRole: 'reader'
      });

      const calendars = response.result.items || [];
      console.log('📊 Calendarios encontrados:', calendars.length);
      
      // Log de información de cada calendario
      calendars.forEach((calendar: any, index: number) => {
        console.log(`📅 ${index + 1}. ${calendar.summary} (${calendar.id})`);
      });

      return calendars;
    } catch (error) {
      console.error('Error obteniendo calendarios:', error);
      throw new Error('Error al obtener la lista de calendarios');
    }
  }

  /**
   * Obtiene la lista de calendarios del propietario específicamente
   * Esto permite que cualquier usuario autorizado vea los calendarios del propietario
   */
  async getOwnerCalendars(): Promise<any[]> {
    try {
      console.log('👑 Obteniendo calendarios del propietario:', CONFIG.owner.email);
      
      const gapi = this.authManager.getGapiClient();
      if (!gapi) {
        throw new Error('Cliente de Google API no disponible');
      }

      if (!gapi.calendar) {
        throw new Error('gapi.client.calendar no está disponible');
      }

      // Obtener todos los calendarios disponibles para el usuario actual
      const ownerCalendars: any[] = [];
      
      try {
        const response = await gapi.calendar.calendarList.list({
          minAccessRole: 'reader'
        });

        const allCalendars = response.result.items || [];
        console.log('📋 Calendarios disponibles para el usuario actual:', allCalendars.length);
        
        // Filtrar calendarios que pertenezcan al propietario o estén relacionados
        const relevantCalendars = allCalendars.filter((calendar: any) => {
          const isOwnerCalendar = calendar.id === CONFIG.owner.email;
          const isSharedByOwner = calendar.accessRole && ['owner', 'reader', 'writer'].includes(calendar.accessRole);
          const isRelevant = isOwnerCalendar || isSharedByOwner;
          
          if (isRelevant) {
            console.log(`✅ Calendario relevante encontrado: ${calendar.summary} (${calendar.id})`);
          }
          
          return isRelevant;
        });

        ownerCalendars.push(...relevantCalendars);

      } catch (error) {
        console.warn('⚠️ Error obteniendo lista de calendarios:', error);
      }

      // Si no encontramos calendarios relevantes, intentar acceso directo al calendario principal
      if (ownerCalendars.length === 0) {
        try {
          console.log('🔄 Intentando acceso directo al calendario principal del propietario...');
          const primaryResponse = await gapi.calendar.calendars.get({
            calendarId: CONFIG.owner.email
          });
          
          if (primaryResponse.result) {
            ownerCalendars.push({
              id: CONFIG.owner.email,
              summary: primaryResponse.result.summary || 'Calendario Principal',
              backgroundColor: '#1976D2',
              accessRole: 'reader'
            });
            console.log('✅ Acceso directo al calendario principal exitoso');
          }
        } catch (error) {
          console.warn('⚠️ No se pudo acceder al calendario principal del propietario. Esto es normal si no se han compartido los calendarios.');
          console.info('💡 SOLUCIÓN: El propietario debe compartir sus calendarios con este usuario.');
        }
      }

      console.log(`📊 Calendarios del propietario encontrados: ${ownerCalendars.length}`);
      
      // Log de información de cada calendario del propietario
      ownerCalendars.forEach((calendar: any, index: number) => {
        console.log(`👑 ${index + 1}. ${calendar.summary} (${calendar.id})`);
      });

      // Si no hay calendarios disponibles, mostrar mensaje informativo
      if (ownerCalendars.length === 0) {
        console.info('📢 INFORMACIÓN: No se encontraron calendarios del propietario.');
        console.info('📢 POSIBLES CAUSAS:');
        console.info('   1. Los calendarios no han sido compartidos');
        console.info('   2. El usuario actual no tiene permisos');
        console.info('   3. Error de configuración en Google Calendar');
        console.info('📢 SOLUCIÓN: Verificar que los calendarios estén compartidos correctamente');
      }

      return ownerCalendars;
      
    } catch (error) {
      console.error('❌ Error obteniendo calendarios del propietario:', error);
      console.info('🔄 Fallback: Intentando obtener calendarios del usuario actual...');
      
      // Fallback: intentar obtener los calendarios del usuario actual
      try {
        return await this.getUserCalendars();
      } catch (fallbackError) {
        console.error('❌ Error en fallback:', fallbackError);
        return [];
      }
    }
  }

  /**
   * Obtiene los eventos de la semana actual de todos los calendarios del propietario
   */
  async getWeekEvents(): Promise<CalendarEvent[]> {
    try {
      console.log('📅 Obteniendo eventos de la semana de los calendarios del propietario...');
      
      const weekEnd = this.getWeekEnd(this.currentWeekStart);
      
      const queryOptions: CalendarQueryOptions = {
        timeMin: this.currentWeekStart,
        timeMax: weekEnd,
        singleEvents: true,
        orderBy: 'startTime',
        maxResults: 250
      };

      // Obtener calendarios del propietario en lugar del usuario actual
      const calendars = await this.getOwnerCalendars();
      
      // Obtener eventos de todos los calendarios del propietario
      const allEvents: CalendarEvent[] = [];
      
      for (const calendar of calendars) {
        try {
          console.log(`📊 Obteniendo eventos del calendario del propietario: ${calendar.summary}`);
          const events = await this.fetchCalendarEventsFromSpecificCalendar(calendar.id, queryOptions);
          
          // Agregar información del calendario a cada evento
          const eventsWithCalendarInfo = events.map((event: CalendarEvent) => ({
            ...event,
            calendarName: calendar.summary,
            calendarId: calendar.id,
            calendarColor: calendar.backgroundColor || '#1976D2'
          }));
          
          allEvents.push(...eventsWithCalendarInfo);
        } catch (calendarError) {
          console.warn(`⚠️ Error obteniendo eventos del calendario ${calendar.summary}:`, calendarError);
          // Continuar con otros calendarios aunque uno falle
        }
      }
      
      // Ordenar todos los eventos por fecha
      allEvents.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
      
      console.log(`📊 Total de eventos obtenidos: ${allEvents.length}`);
      return allEvents;
    } catch (error) {
      throw this.createAppError('FETCH_EVENTS_FAILED', 'Error al obtener eventos del calendario', error);
    }
  }

  /**
   * Obtiene eventos de un calendario específico
   */
  private async fetchCalendarEventsFromSpecificCalendar(calendarId: string, queryOptions: CalendarQueryOptions): Promise<CalendarEvent[]> {
    try {
      const gapi = this.authManager.getGapiClient();
      
      const response = await gapi.calendar.events.list({
        calendarId: calendarId,
        timeMin: queryOptions.timeMin.toISOString(),
        timeMax: queryOptions.timeMax.toISOString(),
        singleEvents: queryOptions.singleEvents,
        orderBy: queryOptions.orderBy,
        maxResults: queryOptions.maxResults
      });

      const items = response.result.items || [];
      return items.map((item: GoogleCalendarEventItem) => this.transformGoogleEventToCalendarEvent(item));
    } catch (error) {
      console.error(`Error obteniendo eventos del calendario ${calendarId}:`, error);
      throw error;
    }
  }

  /**
   * Obtiene los eventos del día actual del propietario
   */
  async getTodayEvents(): Promise<CalendarEvent[]> {
    try {
      console.log('📅 Obteniendo eventos de hoy del propietario...');
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const queryOptions: CalendarQueryOptions = {
        timeMin: today,
        timeMax: tomorrow,
        singleEvents: true,
        orderBy: 'startTime',
        maxResults: 100
      };

      // Obtener calendarios del propietario en lugar del usuario actual
      const calendars = await this.getOwnerCalendars();
      
      // Obtener eventos de todos los calendarios del propietario
      const allEvents: CalendarEvent[] = [];
      
      for (const calendar of calendars) {
        try {
          const events = await this.fetchCalendarEventsFromSpecificCalendar(calendar.id, queryOptions);
          
          // Agregar información del calendario a cada evento
          const eventsWithCalendarInfo = events.map((event: CalendarEvent) => ({
            ...event,
            calendarName: calendar.summary,
            calendarId: calendar.id,
            calendarColor: calendar.backgroundColor || '#1976D2'
          }));
          
          allEvents.push(...eventsWithCalendarInfo);
        } catch (calendarError) {
          console.warn(`⚠️ Error obteniendo eventos del calendario ${calendar.summary}:`, calendarError);
        }
      }
      
      // Ordenar todos los eventos por fecha
      allEvents.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
      
      console.log(`📊 Eventos de hoy encontrados: ${allEvents.length}`);
      return allEvents;
    } catch (error) {
      throw this.createAppError('FETCH_TODAY_EVENTS_FAILED', 'Error al obtener eventos de hoy', error);
    }
  }

  /**
   * Navega a la semana anterior
   */
  navigateToPreviousWeek(): void {
    console.log('⬅️ Navegando a la semana anterior...');
    this.currentWeekStart.setDate(this.currentWeekStart.getDate() - 7);
  }

  /**
   * Navega a la semana siguiente
   */
  navigateToNextWeek(): void {
    console.log('➡️ Navegando a la semana siguiente...');
    this.currentWeekStart.setDate(this.currentWeekStart.getDate() + 7);
  }

  /**
   * Navega a la semana actual
   */
  navigateToCurrentWeek(): void {
    console.log('🏠 Navegando a la semana actual...');
    this.currentWeekStart = this.getMondayOfCurrentWeek();
  }

  /**
   * Obtiene la información de la semana actual
   */
  getCurrentWeekInfo(): WeekInfo {
    const weekEnd = this.getWeekEnd(this.currentWeekStart);
    
    return {
      start: this.currentWeekStart,
      end: weekEnd,
      isCurrentWeek: this.isCurrentWeek(this.currentWeekStart)
    };
  }

  /**
   * Obtiene el lunes de la semana actual
   */
  private getMondayOfCurrentWeek(): Date {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Ajuste para el domingo
    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  }

  /**
   * Obtiene el domingo (fin de semana) de una fecha dada
   */
  private getWeekEnd(weekStart: Date): Date {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    return weekEnd;
  }

  /**
   * Verifica si una fecha corresponde a la semana actual
   */
  private isCurrentWeek(weekStart: Date): boolean {
    const currentWeekStart = this.getMondayOfCurrentWeek();
    return weekStart.getTime() === currentWeekStart.getTime();
  }

  /**
   * Transforma un evento de Google Calendar al formato de la aplicación
   */
  private transformGoogleEventToCalendarEvent(googleEvent: GoogleCalendarEventItem): CalendarEvent {
    try {
      const startTime = this.parseEventDateTime(googleEvent.start);
      const endTime = this.parseEventDateTime(googleEvent.end);
      
      // Determinar si es un evento de todo el día
      const isAllDay = Boolean(googleEvent.start.date);

      const event: CalendarEvent = {
        id: googleEvent.id,
        title: googleEvent.summary || 'Sin título',
        startTime: startTime,
        endTime: endTime,
        isAllDay: isAllDay,
        status: this.mapEventStatus(googleEvent.status),
        description: googleEvent.description || '',
        location: googleEvent.location || '',
        attendees: this.parseAttendees(googleEvent.attendees)
      };

      return event;
    } catch (error) {
      console.warn('Error transformando evento:', error, googleEvent);
      throw this.createAppError('TRANSFORM_EVENT_FAILED', 'Error al transformar evento de Google Calendar');
    }
  }

  /**
   * Parsea la fecha y hora de un evento de Google Calendar
   */
  private parseEventDateTime(dateTimeObj: any): Date {
    try {
      if (!dateTimeObj) {
        throw new Error('Objeto de fecha/hora no definido');
      }

      // Si tiene dateTime, usar eso (evento con hora específica)
      if (dateTimeObj.dateTime) {
        return new Date(dateTimeObj.dateTime);
      }
      
      // Si solo tiene date, es un evento de todo el día
      if (dateTimeObj.date) {
        return new Date(dateTimeObj.date + 'T00:00:00');
      }

      throw new Error('Formato de fecha/hora no reconocido');
    } catch (error) {
      console.error('Error parseando fecha/hora del evento:', error, dateTimeObj);
      return new Date(); // Fecha por defecto
    }
  }

  /**
   * Mapea el estado del evento de Google Calendar
   */
  private mapEventStatus(status: string | undefined): EventStatus {
    switch (status) {
      case 'confirmed':
        return EventStatus.CONFIRMED;
      case 'tentative':
        return EventStatus.TENTATIVE;
      case 'cancelled':
        return EventStatus.CANCELLED;
      default:
        return EventStatus.CONFIRMED; // Estado por defecto
    }
  }

  /**
   * Procesa la lista de asistentes de un evento
   */
  private parseAttendees(attendees: any[] | undefined): Array<{
    email: string;
    displayName?: string;
    responseStatus: AttendeeResponseStatus;
    organizer?: boolean;
  }> {
    if (!attendees || !Array.isArray(attendees)) {
      return [];
    }

    return attendees.map(attendee => ({
      email: attendee.email || '',
      displayName: attendee.displayName,
      responseStatus: this.mapAttendeeResponseStatus(attendee.responseStatus),
      organizer: Boolean(attendee.organizer)
    }));
  }

  /**
   * Mapea el estado de respuesta del asistente
   */
  private mapAttendeeResponseStatus(status: string | undefined): AttendeeResponseStatus {
    switch (status) {
      case 'needsAction':
        return AttendeeResponseStatus.NEEDS_ACTION;
      case 'declined':
        return AttendeeResponseStatus.DECLINED;
      case 'tentative':
        return AttendeeResponseStatus.TENTATIVE;
      case 'accepted':
        return AttendeeResponseStatus.ACCEPTED;
      default:
        return AttendeeResponseStatus.NEEDS_ACTION; // Estado por defecto
    }
  }

  /**
   * Crea un error de aplicación con contexto
   */
  private createAppError(code: string, message: string, originalError?: any): Error {
    const error = new Error(message);
    
    if (originalError) {
      console.error(`[${code}] ${message}:`, originalError);
    }
    
    return error;
  }
}